import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'

type LogoTone = 'light' | 'dark'

interface LogoMarkProps {
    className?: string
    tone?: LogoTone
}

interface LogoProps {
    className?: string
    tone?: LogoTone
    onNavigate?: () => void
}

/* Brand colors from the EDITAMA.TECH logo. On dark grounds the navy
   bands switch to paper so the mark keeps its shape. */
const BRAND = {
    navy: '#0B1F3D',
    steel: '#18708F',
    teal: '#3BC2C7',
    amber: '#E9B04E',
    paper: '#F5F0E8'
}

export const LogoMark: FC<LogoMarkProps> = ({ className, tone = 'light' }): ReactNode => {
    const navy = tone === 'dark' ? BRAND.paper : BRAND.navy

    return (
        <svg
            viewBox='0 0 178 184'
            className={className}
            aria-hidden='true'
            focusable='false'
        >
            <polygon fill={navy} points='52,0 135,0 135,42 21.67,42' />
            <polygon fill={BRAND.steel} points='21.67,42 50.33,42 72,72 0,72' />
            <polygon fill={navy} points='135,42 178,42 178,72 135,72' />
            <polygon fill={BRAND.amber} points='152,0 178,0 178,27 152,27' />
            <polygon fill={BRAND.teal} points='0,72 178,72 178,112 0,112' />
            <polygon fill={BRAND.steel} points='0,112 72,112 50.33,142 21.67,142' />
            <polygon fill={navy} points='21.67,142 178,142 178,184 52,184' />
        </svg>
    )
}

const Logo: FC<LogoProps> = ({ className, tone = 'light', onNavigate }): ReactNode => {
    return (
        <Link
            href='/'
            onClick={onNavigate}
            aria-label='EDITAMA.TECH home'
            className={cn('group inline-flex items-center gap-3 rounded-sm', className)}
        >
            <LogoMark
                tone={tone}
                className='h-7 w-auto shrink-0 transition-transform duration-300 ease-editorial group-hover:-translate-y-px'
            />

            <span className='font-mono text-[0.9375rem] font-semibold leading-none tracking-[-0.01em]'>
                editama.tech
            </span>
        </Link>
    )
}

export default Logo
